import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import api from "../../../../axiosconfig.js";

// Date helpers
const startOfMonth = (d) =>
  new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
const endOfMonth = (d) =>
  new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
const endOfDay = (d) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
const fmtMonth = (d) =>
  d.toLocaleString(undefined, { month: "long", year: "numeric" });
const fmtRange = (a, b) => {
  const same =
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
  return same
    ? fmtMonth(a)
    : `${a.toLocaleString(undefined, {
      month: "short",
      year: "numeric",
    })} – ${b.toLocaleString(undefined, {
      month: "short",
      year: "numeric",
    })}`;
};
const toYmd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const WaveLoadingDots = () => (
  <span className="wave-loading-dots" aria-label="Loading">
    <span className="wave-dot" />
    <span className="wave-dot" />
    <span className="wave-dot" />
  </span>
);

// Component
const UserStat = () => {
  const [dataViewMode, setDataViewMode] = useState("current");

  const today = useMemo(() => new Date(), []);
  const effectiveToday = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 0);
  }, []);

  const thisMonthStart = useMemo(() => startOfMonth(today), [today]);
  const thisMonthEnd = useMemo(() => endOfDay(today), [today]);
  const lastCompletedMonthEnd = useMemo(
    () => endOfMonth(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
    [today]
  );

  const [rangeStart, setRangeStart] = useState(thisMonthStart);
  const [rangeEnd, setRangeEnd] = useState(thisMonthEnd);
  const [focusDate, setFocusDate] = useState(thisMonthEnd);

  const [logins, setLogins] = useState(0);
  const [currLoginStreak, setCurrLoginStreak] = useState(0);
  const [maxLoginStreak, setMaxLoginStreak] = useState(0);
  const [loading, setLoading] = useState(false);

  // API CALL
  const fetchUserLogins = async (startDate, endDate) => {
    setLoading(true);
    try {
      const response = await api.get("/api/login-activity/summary", {
        params: {
          fromDate: toYmd(startDate),
          toDate: toYmd(endDate),
        },
      });
      const data = response.data || {};
      setLogins(Number(data.totalActiveDays) || 0);
      setCurrLoginStreak(Number(data.currentLoginStreak) || 0);
      setMaxLoginStreak(Number(data.largestLoginStreak) || 0);
    } catch (err) {
      console.error(err);
      setLogins(0);
      setCurrLoginStreak(0);
      setMaxLoginStreak(0);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchUserLogins(thisMonthStart, thisMonthEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handlers
  const handleIntervalChange = (mode) => {
    setDataViewMode(mode);

    if (mode === "current") {
      setRangeStart(thisMonthStart);
      setRangeEnd(thisMonthEnd);
      setFocusDate(thisMonthEnd);
      fetchUserLogins(thisMonthStart, thisMonthEnd);
    }

    if (mode === "range") {
      const prev2Start = startOfMonth(
        new Date(effectiveToday.getFullYear(), effectiveToday.getMonth() - 1, 1)
      );
      const prev1End = endOfMonth(effectiveToday);

      setRangeStart(prev2Start);
      setRangeEnd(prev1End);
      setFocusDate(prev1End);
      fetchUserLogins(prev2Start, prev1End);
    }
  };

  const handleRangePick = (dates) => {
    const [start, end] = dates || [];
    const safeStart = start
      ? new Date(Math.min(start.getTime(), lastCompletedMonthEnd.getTime()))
      : null;
    const safeEnd = end
      ? new Date(Math.min(end.getTime(), lastCompletedMonthEnd.getTime()))
      : null;

    if (safeStart && safeEnd && safeStart > safeEnd) {
      return;
    }

    setRangeStart(safeStart);
    setRangeEnd(safeEnd);
    if (safeStart) setFocusDate(safeStart);

    if (safeStart && safeEnd) {
      const normalizedStart = startOfMonth(safeStart);
      const normalizedEnd = endOfMonth(safeEnd);
      fetchUserLogins(normalizedStart, normalizedEnd);
    }
  };

  // Labels 
  const displayDateString = useMemo(() => {
    if (dataViewMode === "current") return fmtMonth(thisMonthStart);
    if (rangeStart && rangeEnd)
      return fmtRange(startOfMonth(rangeStart), endOfMonth(rangeEnd));
    return "Select a month range";
  }, [dataViewMode, rangeStart, rangeEnd, thisMonthStart]);

  return (
    <div className="home-statistics-group-container">
      <div className="home-statistics-section-container">
        <div className="home-statistics-section-header">
          <div className="home-statistics-section-text-container">
            <div className="home-statistics-section-title">
              User Statistics ({displayDateString})
            </div>
            <div className="home-statistics-section-subtitle">
              Key insights into your well-being
            </div>
          </div>

          <div className="home-menu-container">
            <div className="home-statistics-section-interval-button-container">
              <div
                className={`home-statistics-section-interval-button ${dataViewMode === "current" ? "active" : ""
                  }`}
                onClick={() => handleIntervalChange("current")}
              >
                Current Month
              </div>
              <div
                className={`home-statistics-section-interval-button ${dataViewMode === "range" ? "active" : ""
                  }`}
                onClick={() => handleIntervalChange("range")}
              >
                Month Range
              </div>
            </div>

            <div
              className="home-daterange-button"
              id="daterange"
              style={{ display: dataViewMode === "range" ? "flex" : "none" }}
            >
              <DatePicker
                id="datepicker"
                className="home-datepicker"
                selectsRange
                showMonthYearPicker
                dateFormat="MMMM yyyy"
                startDate={rangeStart || undefined}
                endDate={rangeEnd || undefined}
                onChange={handleRangePick}
                minDate={new Date(2023, 0, 1)}
                maxDate={lastCompletedMonthEnd}
                openToDate={focusDate}
              />
              <div className="home-daterange-button-icon">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8.2 9.6C7.97333 9.6 7.78347 9.5232 7.6304 9.3696C7.4768 9.21653 7.4 9.02667 7.4 8.8C7.4 8.57333 7.4768 8.3832 7.6304 8.2296C7.78347 8.07653 7.97333 8 8.2 8C8.42667 8 8.6168 8.07653 8.7704 8.2296C8.92347 8.3832 9 8.57333 9 8.8C9 9.02667 8.92347 9.21653 8.7704 9.3696C8.6168 9.5232 8.42667 9.6 8.2 9.6ZM5 9.6C4.77333 9.6 4.5832 9.5232 4.4296 9.3696C4.27653 9.21653 4.2 9.02667 4.2 8.8C4.2 8.57333 4.27653 8.3832 4.4296 8.2296C4.5832 8.07653 4.77333 8 5 8C5.22667 8 5.4168 8.07653 5.5704 8.2296C5.72347 8.3832 5.8 8.57333 5.8 8.8C5.8 9.02667 5.72347 9.21653 5.5704 9.3696C5.4168 9.5232 5.22667 9.6 5 9.6ZM11.4 9.6C11.1733 9.6 10.9835 9.5232 10.8304 9.3696C10.6768 9.21653 10.6 9.02667 10.6 8.8C10.6 8.57333 10.6768 8.3832 10.8304 8.2296C10.9835 8.07653 11.1733 8 11.4 8C11.6267 8 11.8165 8.07653 11.9696 8.2296C12.1232 8.3832 12.2 8.57333 12.2 8.8C12.2 9.02667 12.1232 9.21653 11.9696 9.3696C11.8165 9.5232 11.6267 9.6 11.4 9.6ZM8.2 12.8C7.97333 12.8 7.78347 12.7232 7.6304 12.5696C7.4768 12.4165 7.4 12.2267 7.4 12C7.4 11.7733 7.4768 11.5835 7.6304 11.4304C7.78347 11.2768 7.97333 11.2 8.2 11.2C8.42667 11.2 8.6168 11.2768 8.7704 11.4304C8.92347 11.5835 9 11.7733 9 12C9 12.2267 8.92347 12.4165 8.7704 12.5696C8.6168 12.7232 8.42667 12.8 8.2 12.8ZM5 12.8C4.77333 12.8 4.5832 12.7232 4.4296 12.5696C4.27653 12.4165 4.2 12.2267 4.2 12C4.2 11.7733 4.27653 11.5835 4.4296 11.4304C4.5832 11.2768 4.77333 11.2 5 11.2C5.22667 11.2 5.4168 11.2768 5.5704 11.4304C5.72347 11.5835 5.8 11.7733 5.8 12C5.8 12.2267 5.72347 12.4165 5.5704 12.5696C5.4168 12.7232 5.22667 12.8 5 12.8ZM11.4 12.8C11.1733 12.8 10.9835 12.7232 10.8304 12.5696C10.6768 12.4165 10.6 12.2267 10.6 12C10.6 11.7733 10.6768 11.5835 10.8304 11.4304C10.9835 11.2768 11.1733 11.2 11.4 11.2C11.6267 11.2 11.8165 11.2768 11.9696 11.4304C12.1232 11.5835 12.2 11.7733 12.2 12C12.2 12.2267 12.1232 12.4165 11.9696 12.5696C11.8165 12.7232 11.6267 12.8 11.4 12.8ZM2.6 16C2.16 16 1.7832 15.8435 1.4696 15.5304C1.15653 15.2168 1 14.84 1 14.4V3.2C1 2.76 1.15653 2.38347 1.4696 2.0704C1.7832 1.7568 2.16 1.6 2.6 1.6H3.4V0H5V1.6H11.4V0H13V1.6H13.8C14.24 1.6 14.6168 1.7568 14.9304 2.0704C15.2435 2.38347 15.4 2.76 15.4 3.2V14.4C15.4 14.84 15.2435 15.2168 14.9304 15.5304C14.6168 15.8435 14.24 16 13.8 16H2.6ZM2.6 14.4H13.8V6.4H2.6V14.4Z" fill="#AAAAAA" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="home-statistics-section-item-container user-stat-tiles">
          <div className="home-statistics-section-item">
            <div className="home-statistics-section-item-header">
              <div className="home-statistics-section-item-title">
                Days Logged In
              </div>
              <div className="home-statistics-section-item-subtitle">
                Total Days This Month
              </div>
            </div>
            <div className="home-statistics-section-item-body-container">
              <div className="home-statistics-section-item-body-text">
                {loading ? <WaveLoadingDots /> : logins}
              </div>
            </div>
          </div>

          <div className="home-statistics-section-item">
            <div className="home-statistics-section-item-header">
              <div className="home-statistics-section-item-title">
                Current Streak
              </div>
              <div className="home-statistics-section-item-subtitle">
                Your Current Login Streak
              </div>
            </div>
            <div className="home-statistics-section-item-body-container">
              <div className="home-statistics-section-item-body-text">
                {loading ? <WaveLoadingDots /> : currLoginStreak}
              </div>
            </div>
          </div>

          <div className="home-statistics-section-item">
            <div className="home-statistics-section-item-header">
              <div className="home-statistics-section-item-title">
                Largest Streak
              </div>
              <div className="home-statistics-section-item-subtitle">
                Your Longest Login Streak
              </div>
            </div>
            <div className="home-statistics-section-item-body-container">
              <div className="home-statistics-section-item-body-text">
                {loading ? <WaveLoadingDots /> : maxLoginStreak}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserStat;
